// Matches one or more consecutive leading `[YYYY-MM-DD Day HH:MM]` timestamp prefixes,
// each followed by a newline or space/tab separator. Anchored at position 0 only, so a
// legitimate reply that references a bracketed date mid-sentence is left untouched.
//
// Legacy DB rows written before a pagination fix can carry several stacked prefixes, e.g.
//   [2026-04-07 Tue 20:10]\n[2026-04-07 Tue 20:45]\n\nActual content
// The injector always emitted `\n` as the separator; `[ \t]+` is accepted too so any
// historical rogue path that injected with a space still gets cleaned.
const STACKED_LEADING_TIMESTAMP_PREFIX = /^(?:\[\d{4}-\d{2}-\d{2} [A-Z][a-z]{2} \d{2}:\d{2}\](?:\n+|[ \t]+))+/;

/**
 * The minimal shape a chat message content part must have for prefix stripping — a
 * discriminant `type` plus an optional `text`. Kept generic so callers can pass their own
 * richer part type through unchanged.
 */
export type TimestampPrefixedPart = {
	readonly type: string;
	readonly text?: string;
};

/** Result of a one-shot prefix strip: the cleaned text and whether anything was removed. */
type StripResult = {
	readonly output: string | null | undefined;
	readonly stripped: boolean;
};

/**
 * Strip ALL consecutive leading timestamp prefixes from a string. Non-string input passes
 * through unchanged; the flag tells the caller whether anything was actually removed.
 */
function stripAllLeadingTimestampPrefixes(text: string | null | undefined): StripResult {
	if (typeof text !== 'string' || text.length === 0) {
		return { output: text, stripped: false };
	}
	const cleaned = text.replace(STACKED_LEADING_TIMESTAMP_PREFIX, '');
	return { output: cleaned, stripped: cleaned !== text };
}

/**
 * Strip stacked leading timestamp prefixes from the FIRST text-typed item of an array of
 * content parts. Non-text items and later text items pass through untouched — the prefix can
 * only appear at position 0 of a response. Returns the original array reference when no strip
 * happened, so memoized consumers keep referential stability.
 */
export function stripAllLeadingTimestampPrefixesFromParts<T extends TimestampPrefixedPart>(
	parts: readonly T[],
): readonly T[] {
	const firstTextIndex = parts.findIndex(part => part.type === 'text' && typeof part.text === 'string');
	if (firstTextIndex < 0) return parts;

	const original = parts[firstTextIndex].text;
	if (typeof original !== 'string') return parts;

	const { output, stripped } = stripAllLeadingTimestampPrefixes(original);
	if (!stripped || typeof output !== 'string') return parts;

	const copy = parts.slice();
	copy[firstTextIndex] = { ...parts[firstTextIndex], text: output };
	return copy;
}
